import * as React from 'react'
import { IFileDto, IOverviewItemDto } from '../../config/fileConfig'
import { formatFullDate } from '../../helpers/dateHelper'
import IconValue from '../IconValue/IconValue'
import Loading from '../../components/Loading'
import MoneyText from '../Text/Currency/MoneyText'
import useCurrency from '../../hooks/useCurrency'
import useModules from '../../hooks/module/useModules'
import { FileRateType, ModuleType } from '../../context/GlobalContext'
import useRoles, { Role } from '../../hooks/useRoles'
import { isAppContext } from '../../helpers/appHelper'
import { AppContext } from '../../config/appConfig'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import PersonAvatar from '../PersonAvatar'
import { FlowchartRegular, MoneyRegular } from '@fluentui/react-icons'
import useWorkflows from '../../hooks/useWorkflows'
import { workflowInstancesText } from '../../helpers/workflowHelper'
import { makeStyles, Text, tokens, Tooltip } from '@fluentui/react-components'
import useViewport, { Viewport } from '../../hooks/useViewport'
import { toastContainerId, useApplicationStyles } from '../../pages/App'

const useStyles = makeStyles({
    container: {
        display: 'grid',
        gap: tokens.spacingHorizontalM,
    },
    flexContainer: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        padding: tokens.spacingHorizontalM,
    },
    lineOne: {
        display: 'flex',
        gap: tokens.spacingHorizontalL,
    },
    lineTwo: {
        display: 'flex',
        gap: tokens.spacingHorizontalL,
    },
})

type Props = {
    file?: IOverviewItemDto | IFileDto
    showTitle?: boolean
    className?: string
}

export default function FileHeader(props: Props): React.ReactElement {
    const applicationStyles = useApplicationStyles()
    const modules = useModules()
    const roles = useRoles()
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { getWorkflowById } = useWorkflows()

    const { isViewport } = useViewport()

    const { currency, currencyName } = useCurrency(props.file?.currency)

    const copyToSubjectClick = (value: string) => {
        if (isAppContext([AppContext.Compose]) && Office.context.mailbox.item?.subject?.getAsync) {
            Office.context.mailbox.item?.subject?.getAsync(result => {
                if (result.value.toLowerCase().indexOf(value.toLowerCase()) === -1) {
                    Office.context.mailbox.item?.subject.setAsync(`${result.value} ${value}`)
                }
            })
        } else {
            navigator.clipboard.writeText(`${value}`)
            toast(translate('action.copy.copied'), { autoClose: 1000, containerId: toastContainerId })
        }
    }

    const copyToBodyClick = (value: string) => {
        if (isAppContext([AppContext.Compose]) && Office.context.mailbox.item?.body?.prependAsync) {
            Office.context.mailbox.item?.body.prependAsync(value)
        } else {
            navigator.clipboard.writeText(`${value}`)
            toast(translate('action.copy.copied'), { autoClose: 1000, containerId: toastContainerId })
        }
    }

    if (!props.file) {
        return <Loading />
    }

    return (
        <div className={props.showTitle ? styles.container : styles.flexContainer}>
            <div className={styles.lineOne}>
                <PersonAvatar name={props.file.owner?.fullName} color="colorful" />
                {props.showTitle && (
                    <>
                        <Tooltip content={translate('action.copy.email.clipboard')} relationship={'description'} withArrow>
                            <Text
                                className={applicationStyles.alignSelf}
                                size={isViewport('>=', Viewport.OutlookNewWide) ? 600 : 400}
                                onClick={() => copyToSubjectClick(`[${props.file.reference}]`)}
                            >
                                {props.file.reference}
                            </Text>
                        </Tooltip>
                        <Tooltip content={translate('action.copy.email.clipboard')} relationship={'description'} withArrow>
                            <Text
                                className={applicationStyles.alignSelf}
                                size={isViewport('>=', Viewport.OutlookNewWide) ? 600 : 400}
                                onClick={() => copyToSubjectClick(props.file.name)}
                            >
                                {props.file.name}
                            </Text>
                        </Tooltip>
                    </>
                )}
            </div>
            {(props.file as IFileDto).description && props.showTitle && (
                <IconValue textVariant="mediumPlus" iconName="TextDocument">
                    <Tooltip content={translate('action.copy.email.clipboard')} relationship={'description'} withArrow>
                        <Text onClick={() => copyToBodyClick((props.file as IFileDto).description)}>{(props.file as IFileDto).description}</Text>
                    </Tooltip>
                </IconValue>
            )}
            <div className={styles.lineTwo}>
                {modules.has(ModuleType.Invoices) && roles.none([Role.FileLimited]) && (
                    <IconValue
                        textVariant={'small'}
                        icon={<MoneyRegular />}
                        value={
                            props.file.fileRateType === FileRateType.File ? (
                                <MoneyText amount={props.file.rate} currency={currency} />
                            ) : props.file.fileRateType === FileRateType.UserTier ? (
                                `${translate('file.rate.usertier.label')}, ${currencyName} (${currency.symbol})`
                            ) : (
                                `${translate('file.rate.partytier.label')}, ${currencyName} (${currency.symbol})`
                            )
                        }
                    />
                )}
                {modules.has(ModuleType.Workflow) && props.file.workflows?.length > 0 && (
                    <IconValue
                        textVariant={'small'}
                        icon={<FlowchartRegular />}
                        value={workflowInstancesText(props.file.workflows, workflowId => getWorkflowById(workflowId)?.name)}
                    />
                )}

                {(props.file as IFileDto).dateOfIncident && (
                    <IconValue textVariant="mediumPlus" iconName="Calendar">
                        {formatFullDate((props.file as IFileDto).dateOfIncident)}
                    </IconValue>
                )}
            </div>
        </div>
    )
}
